import {useCallback, useMemo} from 'react';

import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {AnalyticsFilterTypesEnum} from '../../../models/api/analyticsSavedViewModel';
import {
  AnalyticsPanelIdSelector,
  getDefaultAnalyticsPanelId,
} from '../../../store/panels/analytics/analyticsPanelSelectors';
import {updateFiltersAction} from '../../../store/panels/analytics/filters/analyticsPanelFiltersActions';
import {makeGetAnalyticsFilter} from '../../../store/panels/analytics/filters/analyticsPanelFiltersSelectors';
import {AnalyticsPanelFiltersState} from '../../../store/panels/analytics/filters/analyticsPanelFiltersState';

/**
 * Custom hook to retrieve filters and dispatch changes.
 */

export function useAnalyticsFilter<T extends AnalyticsFilterTypesEnum>(
  type: T,
  getPanelId: AnalyticsPanelIdSelector<void> = getDefaultAnalyticsPanelId,
): [AnalyticsPanelFiltersState[T], (arg: AnalyticsPanelFiltersState[T]) => void] {
  const panelId = useAppSelector(getPanelId);
  const getAnalyticsFilterValues = useMemo(
    () => makeGetAnalyticsFilter(type, getPanelId),
    [getPanelId, type],
  );

  const filterValues = useAppSelector(getAnalyticsFilterValues);
  const dispatch = useAppDispatch();

  const onUpdate = useCallback(
    (newValue: AnalyticsPanelFiltersState[T]) => {
      dispatch(updateFiltersAction({[type]: newValue}, panelId));
    },
    [dispatch, panelId, type],
  );

  return [filterValues, onUpdate];
}
